// src/components/forms/MyFormLayout.tsx - VERSIÓN CON MEJOR ESPACIADO Y LÍMITES
import React from 'react';
import { 
  Grid, 
  useTheme, 
  useMediaQuery,
  Box,
  Paper,
  Collapse,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

interface FormLayoutProps {
  children: React.ReactNode;
  spacing?: number;
  maxColumns?: 1 | 2 | 3 | 4;
  /** 
   * Si true, centra el contenido horizontal y verticalmente
   * Ideal para formularios de creación/edición simples
   */
  centered?: boolean;
  /**
   * Altura mínima cuando centered es true
   * @default 'calc(100vh - 250px)'
   */
  minHeight?: string;
  /**
   * Ancho máximo del contenido
   * @default '1200px' para multi-columna, '900px' para single
   */
  maxContentWidth?: string;
}

interface FormCardProps {
  children: React.ReactNode;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  elevation?: number;
  variant?: 'elevation' | 'outlined' | 'clean';
  delay?: number;
  /**
   * Si true, centra el contenido del card
   * Útil cuando se usa con FormLayout centered
   */
  centered?: boolean;
  /**
   * Padding interno del card
   */
  padding?: number | string;
}

interface MyFormSectionProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  variant?: 'card' | 'clean' | 'outlined';
  collapsible?: boolean;
  defaultExpanded?: boolean;
  spacing?: number;
  elevation?: number;
  /**
   * Ancho máximo del contenido dentro de la sección
   */
  maxContentWidth?: string;
}

export const MyFormLayout: React.FC<FormLayoutProps> = ({ 
  children, 
  spacing,
  maxColumns = 2,
  centered = true,
  minHeight = 'calc(100vh - 250px)',
  maxContentWidth
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const gridSpacing = spacing ?? (isMobile ? 2 : 3);
  
  // Determinar el ancho máximo basado en las columnas
  const defaultMaxWidth = maxColumns === 1 ? '900px' : 
                          maxColumns === 2 ? '1200px' :
                          maxColumns === 3 ? '1400px' : '1600px';
  
  const finalMaxWidth = maxContentWidth || defaultMaxWidth;
  
  // Si está centrado, envolver en un Box con centrado
  if (centered) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        minHeight: minHeight,
        px: { xs: 2, sm: 3, md: 4 } 
      }}>
        <Box sx={{ 
          width: '100%', 
          maxWidth: finalMaxWidth 
        }}>
          <Grid 
            container 
            spacing={gridSpacing}
            sx={{
              width: '100%',
              margin: 0,
              justifyContent: 'center',
             
            }}
          >
            {children}
          </Grid>
        </Box>
      </Box>
    );
  }
  
  // Comportamiento normal (no centrado)
  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: finalMaxWidth,
      mx: 'auto', // Centrar horizontalmente
      px: { xs: 2, sm: 3, md: 4 } // Padding horizontal
    }}>
      <Grid 
        container 
        spacing={gridSpacing}
        sx={{
          width: '100%',
          margin: 0, 
          '& > .MuiGrid-item': {
            paddingTop: `${gridSpacing * 8}px !important`,
            paddingLeft: `${gridSpacing * 8}px !important`,
            
            ...(isMobile && maxColumns > 1 && {
              xs: 12,
              maxWidth: '100%',
              flexBasis: '100%',
            }),

            ...(theme.breakpoints.up('sm') && {
              sm: maxColumns > 1 ? 6 : 12,
            }),

            ...(theme.breakpoints.up('md') && {
              md: maxColumns === 4 ? 3 : maxColumns === 3 ? 4 : maxColumns === 2 ? 6 : 12,
            }),
          }
        }}
      >
        {children}
      </Grid>
    </Box>
  );
};

export const FormCard: React.FC<FormCardProps> = ({ 
  children, 
  xs = 12, 
  sm, 
  md, 
  lg, 
  xl,
  elevation = 0,
  variant = 'clean',
  delay = 0,
  centered = false,
  padding
}) => {
  const theme = useTheme();
  
  // Padding por defecto más generoso
  const defaultPadding = variant === 'clean' ? 0 : { xs: 3, sm: 4, md: 5 };
  const finalPadding = padding !== undefined ? padding : defaultPadding;
  
  const content = variant === 'clean' ? (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      p: finalPadding 
    }}>
      {children}
    </Box>
  ) : (
    <Paper 
      elevation={variant === 'elevation' ? elevation : 0}
      variant={variant === 'outlined' ? 'outlined' : 'elevation'}
      sx={{ 
        height: '100%',
        p: finalPadding,
        borderRadius: 2,
        ...(variant === 'outlined' && {
          borderColor: theme.palette.divider,
        })
      }}
    >
      {children}
    </Paper>
  );
  
  return (
    <Grid 
      item 
      xs={xs} 
      sm={sm} 
      md={md} 
      lg={lg} 
      xl={xl}
      sx={centered ? { display: 'flex', justifyContent: 'center' } : undefined}
    >
      {content}
    </Grid>
  );
};

export const MyFormSection: React.FC<MyFormSectionProps> = ({
  title,
  subtitle,
  icon,
  children,
  variant = 'card',
  collapsible = false,
  defaultExpanded = true,
  spacing = 3,
  elevation = 1,
  maxContentWidth = '80%'
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  
  const toggleExpanded = () => {
    if (collapsible) {
      setExpanded(!expanded);
    }
  };

  return (
    <Box sx={{
      width: '100%',
      ...(variant === 'outlined'
        ? {
            border: `4px solid ${theme.palette.primary.dark}20`,
            backgroundColor: theme.palette.primary.dark + '05',
          }
        : {}),
      ...(variant === 'card' && {
        border: `1px solid ${theme.palette.divider}`,
      }),
      borderRadius: 3,
      m: 1,
      overflow: 'auto',
      padding: 3,
      }}>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          
          mb: subtitle ? 1 : 2, 
          cursor: collapsible ? 'pointer' : 'default',
          ...(variant !== 'clean' && {
            pb: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
        
        }}
        onClick={toggleExpanded}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          {icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: theme.palette.primary.main + '15',
                color: theme.palette.primary.main,
               
              
              }}
         
            >
              {icon}
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Box 
              component="h3"
              sx={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 600,
                color: theme.palette.text.primary,
                lineHeight: 1.2,
              }}
            >
              {title}
            </Box>
            {subtitle && (
              <Box
                component="p"
                sx={{
                  margin: 0,
                  mt: 0.5,
                  fontSize: '0.875rem',
                  color: theme.palette.text.secondary,
                  lineHeight: 1.4,
                }}
              >
                {subtitle}
              </Box>
            )}
          </Box>
        </Box>

        {collapsible && (
          <Box
            sx={{
              ml: 1,
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
              }
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        )}
      </Box>

      <Collapse in={!collapsible || expanded} timeout="auto" unmountOnExit>
        <Box sx={{ 
          mt: spacing ?? 3,
          mx: 'auto',

          ...(maxContentWidth && {
            maxWidth: maxContentWidth,
          })
        }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

// Componente helper para formularios simples centrados
export const CenteredFormLayout: React.FC<{
  children: React.ReactNode;
  width?: { xs?: number; sm?: number; md?: number; lg?: number };
  minHeight?: string;
  maxWidth?: string;
}> = ({ 
  children, 
  width = { xs: 12, md: 10, lg: 8 }, // Reducido para evitar que sea muy ancho
  minHeight = 'calc(100vh - 250px)',
  maxWidth = '800px' // Ancho máximo por defecto más conservador
}) => {
  return (
    <MyFormLayout centered minHeight={minHeight} maxContentWidth={maxWidth}>
      <FormCard {...width}>
        {children}
      </FormCard>
    </MyFormLayout>
  );
};

// Componente helper para layouts de dos columnas
export const TwoColumnLayout: React.FC<{
  children: React.ReactNode;
  maxWidth?: string;
  gap?: number;
  centered?: boolean;
  minHeight?: string;
  minColumnWidth?: string | number;
}> = ({ 
  children,
  maxWidth = '100%',
  gap = 2,
  centered = true,
  minHeight = 'calc(100vh - 250px)',
  minColumnWidth = '500px'
}) => {
  const theme = useTheme();
  
  // Convertir gap a pixeles para CSS Grid
  const gapInPixels = gap * 8; // Material-UI spacing unit es 8px por defecto
  
  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      justifyContent: centered ? 'center' : 'flex-start',
      alignItems: 'center',
      width: '100%',

      minHeight: centered ? minHeight : 'auto',
    }}>
      <Box sx={{ 
        width: '100%',
        maxWidth: maxWidth,
        px: { xs: 2, sm: 3, md: 4 },
      }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr', // En móvil siempre una columna
              sm: `repeat(auto-fit, minmax(min(100%, ${minColumnWidth}), 1fr))`, // Responsive grid
            },
            gap: `${gapInPixels}px`,
            width: '100%',
            // Asegurar que cada hijo ocupe el 100% de su celda
            '& > *': {
              width: '100%',
              minWidth: 0, // Prevenir overflow
            }
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};


// Alias para compatibilidad
export const SingleColumnLayout = CenteredFormLayout;

export { MyFormLayout as FormLayout };
export default MyFormLayout;